// The administrator's pages, part one: logging in, the frame around them, what waits, every submission, and the review of one.

import { ApiFailure, api, get } from './api.ts';
import { categorySelect, iconPayload, iconPickerHtml, riskHtml, statusChip } from './admin-shared.ts';
import { addButtons, failed, shell } from './admin-frame.ts';
import { renderCategories, renderItemDetail, renderItems } from './admin-items.ts';
import { topbar } from './layout.ts';
import type { AdminScenario, AdminSubmission, AdminSubmissionDetail, CategoryLists, Finding } from './types.ts';
import { ENTRY_KIND_NAME, confirmModal, closeModal, errorBlock, esc, fmtSize, fmtTime, iconHtml, loadingBlock, md, must, onAct, openModal, refresh, startPoll, toast } from './util.ts';


export async function renderAdmin(app: HTMLElement, segments: string[]): Promise<void> {
  app.innerHTML = `${topbar('admin')}<div class="page">${loadingBlock()}</div>`;
  let authenticated: boolean;
  try {
    authenticated = (await get<{ authenticated: boolean }>('/api/admin/session')).authenticated;
  } catch (thrown) {
    app.innerHTML = `${topbar('admin')}<div class="page">${errorBlock(failed(thrown))}</div>`;
    return;
  }
  if (!authenticated) return renderLogin(app);
  const [, section, id] = segments;
  if (section === 'review' && id) return renderReview(app, id);
  if (section === 'submissions') return renderSubmissions(app);
  if (section === 'items') return id ? renderItemDetail(app, id) : renderItems(app);
  if (section === 'categories') return renderCategories(app);
  return renderQueue(app);
}

let endedNote = '';
/** The session ran out while the administrator was working: the login page says so until they are back in. */
export function markSessionEnded(): void {
  endedNote = '登录已过期，请重新登录。';
}

export function renderLogin(app: HTMLElement, note = endedNote): void {
  app.innerHTML = `${topbar('admin')}<main class="page"><div class="login card"><h2 style="margin:0 0 6px;font-size:22px">管理员登录</h2>
    <p class="sub" style="margin-bottom:20px">登录后可以审核提交、管理已发布的内容和分类。</p>${note ? `<div class="note info">${esc(note)}</div>` : ''}
    <form id="login-form"><label class="field"><span>用户名</span><input name="username" autocomplete="username"></label>
    <label class="field"><span>密码</span><input name="password" type="password" autocomplete="current-password"></label><div id="login-message"></div>
    <button class="btn-primary" id="login-go" style="width:100%;height:44px">登录</button></form></div></main>`;
  const form = must<HTMLFormElement>('#login-form');
  (form.elements.namedItem('username') as HTMLInputElement).focus();
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = must<HTMLButtonElement>('#login-go');
    button.disabled = true;
    try {
      await api('POST', '/api/admin/login', { username: (form.elements.namedItem('username') as HTMLInputElement).value, password: (form.elements.namedItem('password') as HTMLInputElement).value });
      endedNote = '';
      refresh();
    } catch (thrown) {
      button.disabled = false;
      must('#login-message').innerHTML = `<div class="note err">${esc(failed(thrown, '无法登录，请稍后再试。'))}</div>`;
    }
  });
}

onAct('logout', async () => {
  try {
    await api('POST', '/api/admin/logout', {});
  } finally {
    location.hash = '#/skills';
  }
});

function row(entry: AdminSubmission): string {
  return `<tr class="click" data-act="goto" data-href="#/admin/review/${esc(entry.id)}"><td><div class="cell-name">${iconHtml(null, entry.kind, 'sm')}
    <div><div>${esc(entry.name)} <span class="ver">${esc(entry.version)}</span></div><div class="dim">${ENTRY_KIND_NAME[entry.kind]}${entry.newVersionOf ? ` · 新版本（当前公开 ${esc(entry.newVersionOf)}）` : ''}</div></div></div></td>
    <td><div>${esc(entry.repo)}</div><div class="dim">${entry.path ? `${esc(entry.path)} · ` : ''}${esc(entry.commit.slice(0, 7))}</div></td>
    <td>${entry.submittedBy === 'admin' ? '管理员' : '用户'}<div class="dim">${fmtTime(entry.submittedAt)}</div></td><td>${statusChip(entry.status)}</td><td>${riskHtml(entry.scanState === 'done' ? entry.risk : null)}</td></tr>`;
}

function table(entries: AdminSubmission[], empty: string): string {
  if (entries.length === 0) return `<div class="empty">${empty}</div>`;
  return `<div class="tbl-wrap"><table class="tbl"><thead><tr><th>名称</th><th>来源</th><th>提交者</th><th>状态</th><th>检测结果</th></tr></thead><tbody>${entries.map(row).join('')}</tbody></table></div>`;
}

// ---- what waits ----

async function renderQueue(app: HTMLElement): Promise<void> {
  let data: { submissions: AdminSubmission[]; openCount: number };
  try {
    data = await get('/api/admin/submissions?state=open');
  } catch (thrown) {
    shell(app, 'queue', 0, errorBlock(failed(thrown)));
    return;
  }
  const empty = '<b>没有待处理的提交</b>新的提交完成安全检测后会出现在这里。';
  shell(app, 'queue', data.openCount, `<div class="page-head" style="margin-bottom:20px"><div><h1 style="font-size:26px">待处理</h1>
    <p class="sub">检测中、检测失败和等待审核的提交。所有提交都需要你审核并发布后才会公开。</p></div>${addButtons}</div><div id="queue">${table(data.submissions, empty)}</div>`);
  let last = JSON.stringify(data);
  startPoll(async () => {
    const next = await get<typeof data>('/api/admin/submissions?state=open');
    const text = JSON.stringify(next);
    if (text === last) return;
    last = text;
    const holder = document.querySelector('#queue');
    if (holder) holder.innerHTML = table(next.submissions, empty);
    const badge = document.querySelector<HTMLElement>('#open-badge');
    if (badge) {
      badge.textContent = String(next.openCount);
      badge.style.display = next.openCount ? '' : 'none';
    }
  }, 6000);
}

// ---- every submission ----

type Filter = 'all' | 'published' | 'rejected';
let filter: Filter = 'all';
const FILTERS: [Filter, string][] = [['all', '全部'], ['published', '已发布'], ['rejected', '已拒绝']];

async function renderSubmissions(app: HTMLElement): Promise<void> {
  try {
    const [list, open] = await Promise.all([get<{ submissions: AdminSubmission[] }>(`/api/admin/submissions?state=${filter}`), get<{ openCount: number }>('/api/admin/submissions?state=open')]);
    shell(app, 'subs', open.openCount, `<div class="page-head" style="margin-bottom:16px"><div><h1 style="font-size:26px">全部提交</h1>
      <p class="sub">用户和管理员的所有提交，包括历史版本和已拒绝的。</p></div>${addButtons}</div>
      <div class="tabs">${FILTERS.map(([key, label]) => `<button class="${key === filter ? 'on' : ''}" data-act="subs-filter" data-filter="${key}">${label}</button>`).join('')}</div>
      <div>${table(list.submissions, '<b>没有记录</b>')}</div>`);
  } catch (thrown) {
    shell(app, 'subs', 0, errorBlock(failed(thrown)));
  }
}
onAct('subs-filter', (element) => {
  filter = (element.dataset.filter as Filter) ?? 'all';
  refresh();
});

// ---- the review of one ----

const KIND_OF_FINDING: Record<Finding['severity'], string> = { low: 'low', medium: 'medium', high: 'high' };

function reportHtml(d: AdminSubmissionDetail): string {
  if (d.scan.state === 'running') {
    return `<div class="card"><h3>安全检测</h3><p style="margin:0;display:flex;gap:10px;align-items:center"><span class="spin" style="color:var(--blue)"></span>正在检测整个提交包（${d.files.length} 个文件）…</p>
      <p class="dim" style="margin:8px 0 0">检测包括静态规则、代码行为分析和大模型辅助分析，完成后进入待审核。${d.scan.attempts > 1 ? `这是第 ${d.scan.attempts} 次检测。` : ''}</p></div>`;
  }
  if (d.scan.state === 'failed') {
    return `<div class="card"><h3>安全检测</h3><div class="note err" style="margin-bottom:12px"><b>检测失败</b>　${esc(d.scan.error ?? '')} 检测完成前无法审核或发布。</div>
      ${d.review.state === 'pending' ? '<button class="btn-primary" data-act="rescan">重新检测</button>' : ''}<span class="dim" style="margin-left:12px">已尝试 ${d.scan.attempts} 次${d.scan.finishedAt ? ` · ${fmtTime(d.scan.finishedAt)}` : ''}</span>
      ${historyHtml(d)}</div>`;
  }
  const report = d.scan.report;
  if (report?.scanner === 'not-run' && report.prompt) return `<div class="card"><h3>安全检测</h3><p>${esc(report.note ?? '提示词模板只有文字，不经过安全检测。')}</p></div>`;
  if (report?.scanner === 'not-run') return '<div class="card"><h3>安全检测</h3><p>这个包是测试导入的，尚未进行安全检测。</p></div>';
  if (!report) return '<div class="card"><h3>安全检测</h3><p class="dim" style="margin:0">没有找到检测报告。</p></div>';
  return `<div class="card"><h3>安全检测报告 <span class="dim" style="float:right;font-weight:400">${fmtTime(Date.parse(report.finishedAt) || (d.scan.finishedAt ?? 0))} 完成 · 仅管理员可见</span></h3>
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px"><span style="font-size:18px">综合结果</span>${riskHtml(report.risk)}<span class="dim">${report.findings.length ? `发现 ${report.findings.length} 项需关注` : '各项检测均未发现问题'}</span></div>
    <div class="engines">${report.engines.map((engine) => `<div class="engine"><b>${esc(engine.name)}</b><span class="dim">${engine.status === 'skipped' ? `未启用${engine.note ? `（${esc(engine.note)}）` : ''}` : engine.status === 'error' ? `出错${engine.note ? `（${esc(engine.note)}）` : ''}` : engine.n ? `${engine.n} 项发现` : '未发现问题'}</span></div>`).join('')}</div>
    ${report.findings.map((finding) => `<div class="finding"><div class="top"><span class="risk ${KIND_OF_FINDING[finding.severity]}">${finding.severity === 'high' ? '高风险' : finding.severity === 'medium' ? '中风险' : '低风险'}</span>
      <b style="font-weight:500">${esc(finding.title)}</b><span class="where">${esc(finding.engine)} · ${esc(finding.file)}${finding.line ? `:${finding.line}` : ''}</span></div><p>${esc(finding.detail)}</p>${finding.fix ? `<p class="fix">处理建议：${esc(finding.fix)}</p>` : ''}</div>`).join('')}
    <details style="margin-top:14px"><summary class="dim" style="cursor:pointer">检测覆盖的文件（${report.files.length}）</summary><ul class="files" style="margin-top:8px">${report.files.map((file) => `<li>${esc(file)}</li>`).join('')}</ul>
      ${report.skipped.length ? `<div class="dim" style="margin-top:8px">未检测的文件：</div><ul class="files">${report.skipped.map((entry) => `<li>${esc(entry.path)}（${esc(entry.reason)}）</li>`).join('')}</ul>` : ''}</details>
    <p class="dim" style="margin:12px 0 0">大模型辅助分析的结果仅供参考，是否发布由你决定。${d.review.state === 'pending' ? '<span style="margin-left:8px"><button class="btn btn-sm" data-act="rescan">重新检测</button></span>' : ''}</p>${historyHtml(d)}</div>`;
}

const CATEGORY_NAME: Record<string, string> = { accounting: '财务与事务', sales: '销售与推广', travel: '出差与活动', documents: '资料与演示', research: '调查与信息收集', creative: '网页、设计与视频' };
const OFFER_NAME: Record<string, string> = { daily: '每天', weekly: '每周', monthly: '每月' };
const WEEKDAY_NAME: Record<string, string> = { mon: '周一', tue: '周二', wed: '周三', thu: '周四', fri: '周五', sat: '周六', sun: '周日' };
const LANGUAGE_NAME: Record<string, string> = { ja: '日语', en: '英语', zh: '中文' };

/** The scenarios the package brings: every text in every language, as the user would meet it, so the reviewer reads them with the skill. */
function scenariosHtml(d: AdminSubmissionDetail): string {
  if (d.scenarios.length === 0) return '';
  const texts = (label: string, value: Record<string, string | undefined>) =>
    `<dt>${label}</dt><dd>${(['ja', 'en', 'zh'] as const).filter((language) => value[language]).map((language) => `<div><span class="dim">${LANGUAGE_NAME[language]}　</span>${esc(value[language] ?? '')}</div>`).join('')}</dd>`;
  const one = (scenario: AdminScenario): string => {
    const needs = [...scenario.needs.skills.map((skill) => `Skill ${skill}`), ...scenario.needs.connectors.map((connector) => `连接器 ${connector}`), ...scenario.needs.files.map((file) => `文件：${file}`)];
    const when = [
      scenario.suggestWhen?.dayOfMonth ? `每月 ${scenario.suggestWhen.dayOfMonth.join('、')} 日提示` : '',
      scenario.suggestWhen?.weekday ? `${scenario.suggestWhen.weekday.map((day) => WEEKDAY_NAME[day] ?? day).join('、')}提示` : '',
      scenario.schedule ? `可提议${OFFER_NAME[scenario.schedule.offer] ?? scenario.schedule.offer}定期执行` : '',
      scenario.minutes ? `约 ${scenario.minutes} 分钟` : '',
    ].filter(Boolean);
    return `<div style="padding:12px 0;border-top:1px solid var(--line)"><div style="margin-bottom:8px"><span class="ver" style="margin-right:6px">${esc(CATEGORY_NAME[scenario.category] ?? scenario.category)}</span><span class="mono" style="font-size:12px">${esc(scenario.id)}</span></div>
      <dl class="kv">${texts('标题', scenario.title)}${texts('能得到什么', scenario.outcome)}${texts('第一句请求', scenario.firstRequest)}
      ${scenario.followUps.map((followUp, index) => texts(`后续句子 ${index + 1}`, followUp)).join('')}
      ${needs.length ? `<dt>另外需要</dt><dd>${needs.map(esc).join('、')}</dd>` : ''}${when.length ? `<dt>时机</dt><dd>${when.map(esc).join('；')}</dd>` : ''}</dl></div>`;
  };
  return `<div class="card"><h3>随包提供的场景（${d.scenarios.length}）</h3>
    <p class="dim" style="margin:0 0 4px">用户会在“新建项目”里看到这些文字，点击后安装本内容并把第一句请求填进对话。请确认文字只涉及本内容能做的事，没有误导。</p>${d.scenarios.map(one).join('')}</div>`;
}

const historyHtml = (d: AdminSubmissionDetail): string =>
  d.scan.history.some((entry) => entry.outcome === 'failed')
    ? `<details style="margin-top:12px"><summary class="dim" style="cursor:pointer">以往的检测记录</summary><ul class="files" style="columns:1;margin-top:8px">${d.scan.history.map((entry) => `<li>第 ${entry.attempt} 次 · ${fmtTime(entry.at)} · ${entry.outcome === 'failed' ? `失败：${esc(entry.detail)}` : '完成'}</li>`).join('')}</ul></details>`
    : '';

function sidePanel(d: AdminSubmissionDetail, categories: CategoryLists): string {
  const current = d.item?.publishedSubmissionId && d.item.publishedSubmissionId !== d.id ? d.item : null;
  if (d.status === 'pending') {
    const notes: string[] = [];
    if (current) notes.push(`<div class="note info">当前公开的版本是 ${esc(current.publishedVersion ?? '')}。发布后，公开页面和 Otoha 将只提供本版本 ${esc(d.version)}，旧版本只在这里保留记录。</div>`);
    if (d.item && !d.item.listed && d.item.publishedSubmissionId) notes.push('<div class="note warn">这个内容目前已下架。发布本版本会让它重新公开。</div>');
    return `<div class="card"><h3>审核</h3>${notes.join('')}
      <label class="field"><span>展示名称</span><input id="pub-name" value="${esc(d.item?.name ?? d.prompt?.title ?? d.name)}"><div class="hint">仓库中读取的名称：${esc(d.prompt ? `${d.prompt.title}（${d.name}）` : d.name)}</div></label>
      <label class="field"><span>简介</span><textarea id="pub-summary">${esc(d.item?.summary ?? d.description)}</textarea><div class="hint">仓库中读取的简介：${esc(d.description)}</div></label>
      ${d.kind === 'prompt' ? '<input type="hidden" id="pub-category" value="">' : `<label class="field"><span>分类</span>${categorySelect('pub-category', categories[d.kind], d.item?.categoryId ?? null)}</label>`}
      ${iconPickerHtml(d.item?.icon ?? null, d.kind)}
      <div class="actions-row" style="margin-top:6px"><button class="btn-primary" style="flex:1" data-act="publish">发布</button><button class="btn-danger" style="flex:1" data-act="reject">拒绝</button></div></div>`;
  }
  if (d.status === 'failed') return `<div class="card"><h3>审核</h3><p class="dim" style="margin:0 0 12px">安全检测没有完成，无法发布。可以先重新检测，或直接拒绝。</p><button class="btn-danger" data-act="reject">拒绝</button></div>`;
  if (d.status === 'scanning') {
    return `<div class="card"><h3>审核</h3><p class="dim" style="margin:0">安全检测完成后才能审核。${d.item?.publishedVersion ? `检测和审核期间，旧版本 ${esc(d.item.publishedVersion)} ${d.item.listed ? '继续公开' : '保持下架'}。` : ''}</p></div>`;
  }
  if (d.status === 'rejected') {
    return `<div class="card"><h3>审核结果</h3><div class="note err">已于 ${fmtTime(d.review.at ?? 0)} 拒绝。${d.review.reason ? `<br>原因：${esc(d.review.reason)}` : '未填写原因。'}</div>
      <p class="dim" style="margin:0">同一版本再次提交时会提示“已提交”。仓库更新后的新版本可以重新提交。</p></div>`;
  }
  return `<div class="card"><h3>审核结果</h3><div class="note ok">已于 ${fmtTime(d.review.at ?? 0)} 发布。</div>${
    d.status === 'old' ? `<p class="dim" style="margin:0">已被更新的版本 ${esc(d.item?.publishedVersion ?? '')} 取代，只在这里保留记录。</p>` : `<a class="btn" href="#/admin/items/${esc(d.itemId)}">管理这个内容</a>`}</div>`;
}

let reviewing: AdminSubmissionDetail | null = null;

async function renderReview(app: HTMLElement, id: string): Promise<void> {
  let d: AdminSubmissionDetail;
  let categories: CategoryLists;
  let open: { openCount: number };
  try {
    [d, categories, open] = await Promise.all([get<AdminSubmissionDetail>(`/api/admin/submissions/${encodeURIComponent(id)}`), get<CategoryLists>('/api/admin/categories'), get<{ openCount: number }>('/api/admin/submissions?state=open')]);
  } catch (thrown) {
    const missing = thrown instanceof ApiFailure && thrown.status === 404;
    shell(app, 'queue', 0, missing ? '<div class="empty"><b>找不到这条提交</b><a class="link" href="#/admin">返回待处理</a></div>' : errorBlock(failed(thrown)));
    return;
  }
  reviewing = d;
  const source = `https://github.com/${d.repo}/tree/${d.commit}${d.path ? `/${d.path}` : ''}`;
  const open_ = d.status === 'scanning' || d.status === 'failed' || d.status === 'pending';
  shell(app, open_ ? 'queue' : 'subs', open.openCount, `<a class="back" href="${open_ ? '#/admin' : '#/admin/submissions'}">‹ 返回</a>
    <div class="detail-head">${iconHtml(d.item?.icon ?? null, d.kind, 'lg')}<div class="grow"><h1 style="font-size:24px">${esc(d.name)} <span class="ver" style="font-size:14px;vertical-align:middle">${esc(d.version)}</span></h1>
      <div class="sub" style="margin-top:2px">${esc(d.description)}</div><div class="meta"><span>${ENTRY_KIND_NAME[d.kind]}</span><span>·</span><span>${d.submittedBy === 'admin' ? '管理员添加' : '用户提交'}</span><span>·</span><span>${fmtTime(d.submittedAt)}</span></div></div>${statusChip(d.status)}</div>
    <div class="review-grid"><div>${reportHtml(d)}
      <div class="card"><h3>提交的版本</h3><dl class="kv"><dt>仓库</dt><dd><a class="link" href="${esc(source)}" target="_blank" rel="noopener noreferrer">github.com/${esc(d.repo)}</a></dd>
        <dt>分支或版本</dt><dd>${esc(d.refInput || '（留空）')} → ${esc(d.refLabel)}</dd><dt>提交编号</dt><dd class="mono">${esc(d.commit)}</dd>
        <dt>仓库内目录</dt><dd>${esc(d.path || '（根目录）')}</dd><dt>包文件</dt><dd>${fmtSize(d.source.size)}</dd><dt>文件校验码</dt><dd class="mono" style="font-size:12px">${esc(d.source.sha256)}</dd></dl>
        <p class="dim" style="margin:12px 0 0">检测、审核和公开下载使用的都是这个固定版本；仓库之后的更新不会替换它。</p></div>
      ${d.prompt ? `<div class="card"><h3>模板正文</h3><div class="dim" style="margin-bottom:6px">${esc(d.prompt.title)} · ${esc(d.prompt.description)}</div>
        <pre class="prompt-text">${esc(d.prompt.text)}</pre><p class="dim" style="margin:12px 0 0">审核时请通读正文：用户选用模板时，这段文字会原样放进消息。</p></div>` : `<div class="card"><h3>包里有什么</h3>
        <p class="dim" style="margin:0 0 10px">在市场里是「${ENTRY_KIND_NAME[d.kind]}」：${d.kind === 'plugin' ? '带页面或使用 Otoha 的功能。' : '只有做法、脚本或服务，没有页面。'}</p>
        <div class="dim">在对话里用的做法</div><div style="margin:4px 0 10px">${d.skills.length ? d.skills.map((skill) => `<span class="ver" style="margin-right:6px">${esc(skill.name)}</span>`).join('') : '<span class="dim">没有</span>'}</div>
        <div class="dim">搜索关键词</div><div style="margin:4px 0 10px">${d.keywords?.length ? d.keywords.map((kw) => `<span class="ver" style="margin-right:6px">${esc(kw)}</span>`).join('') : '<span class="dim">没有</span>'}</div>
        <div class="dim">连接的服务（.mcp.json）</div><div style="margin:4px 0 10px">${(d.services ?? d.servers.map((server) => ({ name: server.name, transport: server.type === 'stdio' ? 'local' as const : 'http' as const, account: server.type !== 'stdio' }))).length
          ? (d.services ?? d.servers.map((server) => ({ name: server.name, transport: server.type === 'stdio' ? 'local' as const : 'http' as const, account: server.type !== 'stdio' }))).map((service) => `<div>${esc(service.name)}<span class="dim"> · ${service.account ? '需要账号' : '在这台电脑上运行'}</span></div>`).join('')
          : '<span class="dim">没有</span>'}</div>
        <div class="dim">自带的页面</div><div style="margin:4px 0 10px">${d.pages?.length ? d.pages.map((page) => `<div>${esc(page.summary)}</div>`).join('') : '<span class="dim">没有。页面来自服务提供的界面，不来自 ChatGPT 的连接编号。</span>'}</div>
        <div class="dim">使用 Otoha 的功能</div><div style="margin:4px 0 10px">${d.appFeatures?.length ? d.appFeatures.map((feature) => `<div>${esc(feature.summary)}</div>`).join('') : '<span class="dim">没有</span>'}</div>
        <div class="dim">这台电脑需要</div><div style="margin:4px 0 0">${d.localRequirements?.length ? d.localRequirements.map((item) => `<div>${esc(item.name)}<span class="dim"> · ${esc(item.detail)}</span></div>`).join('') : '<span class="dim">没有</span>'}</div>
        ${d.chatGptApps?.length ? `<p class="dim" style="margin:12px 0 0">有 ${d.chatGptApps.length} 个 ChatGPT 连接编号。这些编号不能在 Otoha 里连接，上面没有把它们算作服务。</p>` : ''}</div>`}
      ${scenariosHtml(d)}
      ${d.prompt ? '' : `<div class="card md"><h3>说明内容</h3>${md(d.body)}</div>`}</div>
    <div>${sidePanel(d, categories)}</div></div>`);
  if (d.status === 'scanning') {
    startPoll(async () => {
      const next = await get<AdminSubmissionDetail>(`/api/admin/submissions/${encodeURIComponent(id)}`);
      if (next.status !== 'scanning') refresh();
    }, 4000);
  }
}

const reviewId = (): string => reviewing?.id ?? '';

onAct('rescan', async () => {
  try {
    await api('POST', `/api/admin/submissions/${encodeURIComponent(reviewId())}/rescan`, {});
    toast('已重新发起安全检测');
  } catch (thrown) {
    toast(failed(thrown));
  }
  refresh();
});

onAct('publish', () => {
  const d = reviewing;
  if (!d) return;
  const name = must<HTMLInputElement>('#pub-name').value.trim();
  const summary = must<HTMLTextAreaElement>('#pub-summary').value.trim();
  if (!name || !summary) {
    toast('展示名称和简介不能为空');
    return;
  }
  const send = async () => {
    try {
      await api('POST', `/api/admin/submissions/${encodeURIComponent(d.id)}/publish`, { name, summary, categoryId: must<HTMLSelectElement>('#pub-category').value || null, icon: iconPayload() });
      toast(`已发布「${name}」${d.version}`);
    } catch (thrown) {
      toast(failed(thrown));
    }
    refresh();
  };
  const risk = d.scan.report?.risk;
  if (risk === 'high' || risk === 'medium') {
    confirmModal({ title: `确认发布${risk === 'high' ? '高风险' : '中风险'}内容？`, body: `安全检测发现 ${d.scan.report?.findings.length ?? 0} 项需关注的问题。发布后，所有用户都可以在公开页面和 Otoha 中搜索并下载「${esc(name)}」。`, ok: '仍然发布', danger: true }, () => void send());
  } else void send();
});

onAct('reject', () => {
  const d = reviewing;
  if (!d) return;
  openModal(`<div class="modal-head"><h2>拒绝发布</h2><button class="x" data-act="close" aria-label="关闭">×</button></div>
    <p class="modal-sub">拒绝「${esc(d.name)}」${esc(d.version)}。提交的人看不到审核结果，原因只保存在管理员记录里。</p>
    <label class="field"><span>拒绝原因<em>选填</em></span><textarea id="reject-reason" placeholder="例如：脚本会读取私钥并发送到外部"></textarea></label>
    <div class="modal-actions"><button class="btn" data-act="close">取消</button><button class="btn-danger" id="reject-go">拒绝</button></div>`);
  must('#reject-go').onclick = async () => {
    const reason = must<HTMLTextAreaElement>('#reject-reason').value.trim();
    try {
      await api('POST', `/api/admin/submissions/${encodeURIComponent(d.id)}/reject`, { reason });
      closeModal();
      toast('已拒绝');
      refresh();
    } catch (thrown) {
      toast(failed(thrown));
    }
  };
});
